// src/utils/folderLookup.ts
import axios from "axios";
import { getToken } from "./tokenHelper";

export type FolderMeta = { name: string; webUrl?: string };

export async function fetchFolderMeta(driveId: string, itemId: string): Promise<FolderMeta> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(
    driveId
  )}/items/${encodeURIComponent(itemId)}?$select=id,name,webUrl,parentReference`;

  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });

  if (res.status !== 200) {
    throw new Error(`Graph item lookup failed (${res.status})`);
  }

  const name: string = res.data?.name ?? "";
  const webUrl: string | undefined = res.data?.webUrl || undefined;
  return { name, webUrl };
}

export type LinkedFolder = { id: string; driveId: string; webUrl: string; name: string };

/**
 * Resolve a pasted SharePoint / OneDrive folder link to the folder it points to.
 * Fallback for when the OneDrive picker popup can't report back -- e.g. sessions
 * routed through the Defender for Cloud Apps proxy, whose ".mcas.ms" origin the
 * picker script silently rejects.
 */
export async function resolveFolderLink(link: string): Promise<LinkedFolder> {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    throw new Error("That doesn't look like a link. Copy it from SharePoint and paste it again.");
  }

  // The proxy rewrites hosts to e.g. "leoberwick-my.sharepoint.com.mcas.ms"
  url.hostname = url.hostname.replace(/(\.sharepoint\.com)\..*$/i, "$1");
  if (!/\.sharepoint\.com$/i.test(url.hostname)) {
    throw new Error("That isn't a SharePoint or OneDrive link.");
  }

  // Browser-bar links (".../AllItems.aspx?id=/sites/X/Shared Documents/Folder")
  // carry the folder's path in "id"; the folder's own URL is host + that path
  const pathParam = url.searchParams.get("id");
  const itemUrl = pathParam?.startsWith("/") ? new URL(pathParam, url.origin).href : url.href;

  // Graph's /shares accepts any item URL, base64url-encoded with a "u!" prefix
  const bytes = new TextEncoder().encode(itemUrl);
  const b64 = btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""))
    .replace(/=+$/, "")
    .replace(/\//g, "_")
    .replace(/\+/g, "-");

  const token = await getToken();
  const res = await axios.get(
    `https://graph.microsoft.com/v1.0/shares/u!${b64}/driveItem?$select=id,name,webUrl,folder,parentReference`,
    {
      headers: { Authorization: `Bearer ${token}`, Prefer: "redeemSharingLinkIfNecessary" },
      validateStatus: () => true,
    }
  );

  if (res.status !== 200) {
    throw new Error(`Couldn't open that link (${res.status}). Check it's a folder you have access to.`);
  }
  if (!res.data?.folder) {
    throw new Error("That link is to a file, not a folder. Copy the link of the folder itself.");
  }

  return {
    id: res.data.id,
    driveId: res.data.parentReference?.driveId ?? "",
    webUrl: res.data.webUrl,
    name: res.data.name,
  };
}

/**
 * Count the number of folders (not files) in a SharePoint location.
 * Used to detect potential duplicate folder structures before creation.
 */
export async function countFoldersInLocation(driveId: string, itemId: string): Promise<number> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(
    driveId
  )}/items/${encodeURIComponent(itemId)}/children?$select=id,name,folder&$top=999`;

  const res = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    validateStatus: () => true,
  });

  if (res.status !== 200) {
    throw new Error(`Failed to list children (${res.status})`);
  }

  const items = res.data?.value || [];
  // Count only items that have a "folder" property (are folders, not files)
  const folderCount = items.filter((item: any) => item.folder).length;
  return folderCount;
}
